/* Client-side reference tools. Concept selections are not stock or reservations. */
(() => {
  const uiText = element => window.EasternI18n?.sourceText(element) ?? element?.textContent ?? "";
  const key = '__easternJewelryServiceTools';
  if (window[key]) { window[key].initialize(document); return; }
  const occasions = new Set(['everyday', 'gift', 'occasion']);
  const steps = new Set(['preferences', 'matches', 'review']);
  const finders = new Map();
  // Numeric reference facts from https://www.tiffany.com/size-guide/ (2026-10-07).
  // US size, inside diameter mm, inside circumference mm. No interpolation outside this range.
  const ringChart = [
    [3,14.1,44.2],[3.5,14.5,45.5],[4,14.9,46.8],[4.5,15.3,48],
    [5,15.7,49.3],[5.5,16.1,50.6],[6,16.5,51.9],[6.5,16.9,53.1],
    [7,17.3,54.4],[7.5,17.7,55.7],[8,18.1,57],[8.5,18.5,58.3],
    [9,18.9,59.5],[9.5,19.4,60.8],[10,19.8,62.1],[10.5,20.2,63.4],
    [11,20.6,64.6],[11.5,21,65.9],[12,21.4,67.2],[12.5,21.8,68.5],[13,22.2,69.7]
  ];
  const number = value => Number(value).toFixed(1).replace(/\.0$/, '');
  function ringReference(method, raw) {
    if (!['us', 'diameter', 'circumference'].includes(method) || String(raw).trim() === '' || !Number.isFinite(Number(raw))) {
      return { error: 'Choose a size or enter a measurement in millimeters.' };
    }
    const value = Number(raw);
    const column = method === 'us' ? 0 : method === 'diameter' ? 1 : 2;
    const minimum = ringChart[0][column], maximum = ringChart[ringChart.length - 1][column];
    if (value < minimum || value > maximum) {
      return { error: `This guide covers ${number(minimum)}–${number(maximum)} ${method === 'us' ? 'US sizes' : 'mm'}. Check the unit or share your measurement with the team.` };
    }
    const exact = ringChart.find(row => Math.abs(row[column] - value) < 0.001);
    if (method === 'us' && !exact) return { error: 'Choose a listed US size in half-size steps.' };
    const lower = exact || [...ringChart].reverse().find(row => row[column] < value);
    const upper = exact || ringChart.find(row => row[column] > value);
    const size = exact ? `US ${number(exact[0])}` : `US ${number(lower[0])}–${number(upper[0])}`;
    const dimensions = exact
      ? `${number(exact[1])} mm inside diameter · ${number(exact[2])} mm inside circumference`
      : `Your measurement sits between the published references for US ${number(lower[0])} and US ${number(upper[0])}.`;
    const input = method === 'us' ? `Known US ${number(value)}` : `${method === 'diameter' ? 'Inside diameter' : 'Finger circumference'} ${number(value)} mm`;
    return {
      size, dimensions,
      note: exact ? 'Use this as a conversation reference. The actual ring shape, width and fit still need confirmation.' : 'This is a range, not a fitted size. Confirm with a physical sizer instead of rounding automatically.',
      brief: `${input}; ${method === 'us' ? `${number(exact[1])} mm diameter` : size} (reference only)`.slice(0, 100)
    };
  }

  const validOption = (select, value) => [...select.options].some(option => option.value === value);
  const updateHistory = (params, anchor, replace = false) => {
    const target = new URL(location.href);
    target.search = params.toString(); target.hash = anchor;
    try { history[replace ? 'replaceState' : 'pushState']({}, '', target); } catch (_) { /* State still works where History is restricted. */ }
  };

  function mountRing(root) {
    if (root.dataset.toolBound) return;
    root.dataset.toolBound = 'true';
    const form = root.querySelector('[data-ring-form]');
    const method = root.querySelector('[data-ring-method]');
    const us = root.querySelector('[data-ring-us]');
    const measurement = root.querySelector('[data-ring-measure]');
    const result = root.querySelector('[data-ring-result]');
    const error = root.querySelector('[data-ring-error]');
    const clearResult = () => { result.hidden = true; error.hidden = true; measurement.removeAttribute('aria-invalid'); us.removeAttribute('aria-invalid'); };
    const syncMethod = () => {
      const known = method.value === 'us';
      root.querySelector('[data-ring-us-field]').hidden = !known; us.disabled = !known;
      root.querySelector('[data-ring-measure-field]').hidden = known; measurement.disabled = known;
      root.querySelector('[data-ring-measure-label]').textContent = method.value === 'diameter' ? 'Inside diameter, in mm' : 'Finger circumference, in mm';
      const diameter = method.value === 'diameter';
      measurement.min = diameter ? '14.1' : '44.2'; measurement.max = diameter ? '22.2' : '69.7';
      root.querySelector('[data-ring-measure-hint]').textContent = diameter ? 'Measure only the opening, not the outside of the ring. Reference range: 14.1–22.2 mm.' : 'Enter the full length around the finger. Reference range: 44.2–69.7 mm.';
      clearResult();
    };
    method.addEventListener('change', syncMethod);
    measurement.addEventListener('input', clearResult); us.addEventListener('change', clearResult);
    form.addEventListener('submit', event => {
      event.preventDefault();
      const value = ringReference(method.value, method.value === 'us' ? us.value : measurement.value);
      clearResult();
      if (value.error) {
        error.textContent = value.error; error.hidden = false;
        const control = method.value === 'us' ? us : measurement;
        control.setAttribute('aria-invalid', 'true'); control.focus(); return;
      }
      root.querySelector('[data-ring-result-size]').textContent = value.size;
      root.querySelector('[data-ring-result-dimensions]').textContent = value.dimensions;
      root.querySelector('[data-ring-result-note]').textContent = value.note;
      const incoming = new URLSearchParams(location.search);
      const params = new URLSearchParams({ topic: 'New jewelry advice', interest: 'jewelry-consultation', size: value.brief });
      const piece = incoming.get('piece');
      const finder = document.querySelector('[data-jewelry-finder]');
      const validPiece = [...(finder?.querySelectorAll('[data-finder-card]') || [])].some(card => card.dataset.pieceId === piece);
      if (validPiece) params.set('piece', piece);
      if (occasions.has(incoming.get('occasion'))) params.set('occasion', incoming.get('occasion'));
      root.querySelector('[data-ring-inquiry]').href = '/pages/contact?' + params.toString();
      result.hidden = false;
    });
    root.querySelector('[data-ring-reset]').addEventListener('click', () => { form.reset(); syncMethod(); method.focus(); });
    syncMethod();
    const known = (new URLSearchParams(location.search).get('size') || '').trim().match(/^(?:Known\s+)?US\s*(\d+(?:\.5)?)(?:\s|$|[;(])/i);
    if (known && validOption(us, known[1])) us.value = known[1];
    form.hidden = false;
  }

  function mountFinder(root) {
    if (finders.has(root)) return;
    root.dataset.toolBound = 'true';
    const removeListeners = [];
    const listen = (element, type, handler) => {
      element.addEventListener(type, handler);
      removeListeners.push(() => element.removeEventListener(type, handler));
    };
    const occasion = root.querySelector('[data-finder-occasion]');
    const category = root.querySelector('[data-finder-category]');
    const color = root.querySelector('[data-finder-color]');
    const cards = [...root.querySelectorAll('[data-finder-card]')];
    const byId = new Map(cards.map(card => [card.dataset.pieceId, card]));
    const error = root.querySelector('[data-finder-error]');
    let selected = new Set(), step = 'preferences';
    let applied = { category: '', color: '', occasion: root.dataset.defaultOccasion };
    const summary = () => [
      uiText(occasion.selectedOptions[0]), uiText(category.selectedOptions[0]), uiText(color.selectedOptions[0])
    ].join(' · ');
    const matches = card => (!applied.category || card.dataset.category === applied.category) && (!applied.color || card.dataset.color === applied.color);
    const inquiry = (includeSelection = true) => {
      const params = new URLSearchParams({ topic: 'New jewelry advice', interest: applied.occasion === 'gift' ? 'personal-gifting' : 'jewelry-consultation', finder: '1', occasion: applied.occasion });
      if (applied.category) params.set('category', applied.category);
      if (applied.color) params.set('color', applied.color);
      const ids = includeSelection ? [...selected] : [];
      if (ids.length === 1) params.set('piece', ids[0]);
      if (ids.length > 1) params.set('pieces', ids.join(','));
      const size = (new URLSearchParams(location.search).get('size') || '').trim().slice(0, 100);
      if (size) params.set('size', size);
      return '/pages/contact?' + params.toString();
    };
    const reflectSelection = () => {
      cards.forEach(card => {
        const checked = selected.has(card.dataset.pieceId);
        const checkbox = card.querySelector('[data-finder-select]');
        checkbox.checked = checked;
        card.classList.toggle('is-selected', checked);
        checkbox.nextElementSibling.textContent = checked ? 'Selected — remove' : 'Add to my selection';
      });
      root.querySelector('[data-finder-selected-count]').textContent = `${selected.size} of 3 selected`;
      root.querySelectorAll('[data-finder-review]').forEach(button => { button.disabled = !selected.size; });
      root.querySelector('[data-finder-inquiry]').href = inquiry();
      root.querySelector('[data-finder-general-inquiry]').href = inquiry(false);
    };
    const writeState = (replace = false) => {
      const params = new URLSearchParams(location.search);
      ['piece', 'pieces', 'category', 'color', 'occasion', 'finder_step'].forEach(name => params.delete(name));
      params.set('finder', '1'); params.set('finder_step', step); params.set('occasion', applied.occasion);
      if (applied.category) params.set('category', applied.category);
      if (applied.color) params.set('color', applied.color);
      if (selected.size === 1) params.set('piece', [...selected][0]);
      if (selected.size > 1) params.set('pieces', [...selected].join(','));
      updateHistory(params, 'jewelry-finder', replace);
    };
    const render = (focus = false) => {
      root.querySelectorAll('[data-finder-step]').forEach(panel => { panel.hidden = panel.dataset.finderStep !== step; });
      root.querySelectorAll('[data-finder-progress]').forEach(item => {
        if (item.dataset.finderProgress === step) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
      });
      let count = 0;
      cards.forEach(card => { card.hidden = !matches(card); if (!card.hidden) count += 1; });
      root.querySelector('[data-finder-count]').textContent = `${count} ${count === 1 ? 'concept matches' : 'concepts match'} your direction`;
      root.querySelector('[data-finder-summary]').textContent = summary();
      root.querySelector('[data-finder-empty]').hidden = count !== 0;
      const topActions = root.querySelector('[data-finder-back="preferences"]').parentElement;
      topActions.hidden = count === 0;
      root.querySelectorAll('[data-finder-review]').forEach(button => { button.hidden = count === 0; });
      error.hidden = true;
      reflectSelection();
      if (step === 'review') {
        root.querySelector('[data-finder-review-summary]').textContent = summary();
        const container = root.querySelector('[data-finder-review-cards]');
        container.replaceChildren();
        selected.forEach(id => {
          const card = byId.get(id).cloneNode(true);
          card.hidden = false; card.removeAttribute('data-finder-card'); card.classList.remove('is-selected');
          card.querySelector('[data-finder-select]').closest('label').remove();
          container.append(card);
        });
      }
      if (focus) {
        const target = step === 'matches' ? root.querySelector('[data-finder-count]') : step === 'review' ? root.querySelector('[data-finder-review-title]') : occasion;
        target.focus({ preventScroll: true });
        root.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    };
    const readState = () => {
      const params = new URLSearchParams(location.search);
      applied = {
        category: validOption(category, params.get('category')) ? params.get('category') : '',
        color: validOption(color, params.get('color')) ? params.get('color') : '',
        occasion: occasions.has(params.get('occasion')) ? params.get('occasion') : root.dataset.defaultOccasion
      };
      occasion.value = applied.occasion; category.value = applied.category; color.value = applied.color;
      const incoming = ((params.get('pieces') || '') + ',' + (params.get('piece') || '')).split(',');
      selected = new Set([...new Set(incoming.filter(id => byId.has(id) && matches(byId.get(id))))].slice(0, 3));
      step = steps.has(params.get('finder_step')) ? params.get('finder_step') : 'preferences';
      if (step === 'review' && !selected.size) step = 'matches';
      render();
    };
    listen(root.querySelector('[data-finder-form]'), 'submit', event => {
      event.preventDefault();
      applied = { category: category.value, color: color.value, occasion: occasion.value };
      selected = new Set([...selected].filter(id => matches(byId.get(id))));
      step = 'matches'; writeState(); render(true);
    });
    cards.forEach(card => listen(card.querySelector('[data-finder-select]'), 'change', event => {
      const id = card.dataset.pieceId;
      if (event.target.checked && selected.size >= 3) {
        event.target.checked = false; error.textContent = 'Choose up to three designs. Remove one before adding another.'; error.hidden = false; return;
      }
      if (event.target.checked) selected.add(id); else selected.delete(id);
      error.hidden = true; writeState(true); reflectSelection();
    }));
    root.querySelectorAll('[data-finder-review]').forEach(button => listen(button, 'click', () => {
      if (!selected.size) return;
      step = 'review'; writeState(); render(true);
    }));
    root.querySelectorAll('[data-finder-back]').forEach(button => listen(button, 'click', () => {
      step = button.dataset.finderBack;
      occasion.value = applied.occasion; category.value = applied.category; color.value = applied.color;
      writeState(); render(true);
    }));
    root.querySelectorAll('[data-finder-reset]').forEach(button => listen(button, 'click', () => {
      selected.clear(); applied = { category: '', color: '', occasion: root.dataset.defaultOccasion };
      occasion.value = applied.occasion; category.value = ''; color.value = ''; step = 'preferences';
      writeState(); render(true);
    }));
    listen(window, 'popstate', readState);
    finders.set(root, () => {
      removeListeners.forEach(remove => remove());
      delete root.dataset.toolBound;
      finders.delete(root);
    });
    readState(); root.querySelector('[data-finder-enhanced]').hidden = false;
  }

  function initialize(root) {
    for (const [finder, destroy] of finders) if (!finder.isConnected) destroy();
    root.querySelectorAll('[data-ring-size-tool]').forEach(mountRing);
    if (root.matches?.('[data-jewelry-finder]')) mountFinder(root);
    root.querySelectorAll('[data-jewelry-finder]').forEach(mountFinder);
  }
  window[key] = { initialize };
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [finder, destroy] of finders) if (event.target === finder || event.target.contains(finder)) destroy();
  });
})();
